import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import { Loader } from 'Src/components/Loader/Loader';
import { createUserTag, deleteUserTag, getAllUserTags, updateUserTag } from 'Src/helpers/service/admin/userTags';
import { getSchoolUsersCount } from 'Src/helpers/service/admin/users';
import { Tag } from 'Src/models/tag';
import { getUserTagColor, USER_TAG_DEFAULT_COLOR, USER_TAG_PALETTE } from 'Src/helpers/tags/tagColors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTags } from '@fortawesome/free-solid-svg-icons';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import './UserTags.scss';
import { PeopleListPageHeader } from '../PeopleListPageHeader';
import { ListEmptyState } from 'Src/components/ListEmptyState/ListEmptyState';

interface Props {
  user: AppUser;
}

interface TagWithCount extends Tag {
  count?: number;
}

interface State {
  items: TagWithCount[];
  isLoading: boolean;
  query: string;
  appliedQuery: string;
  isCreateModalOpen: boolean;
  newName: string;
  newColor: string;
  editingId: string;
  editName: string;
  editColor: string;
  actionMenuId: string;
  removingTag: TagWithCount | null;
  error: string;
  createError: string;
}

export class UserTags extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      isLoading: true,
      query: '',
      appliedQuery: '',
      isCreateModalOpen: false,
      newName: '',
      newColor: USER_TAG_DEFAULT_COLOR,
      editingId: '',
      editName: '',
      editColor: USER_TAG_DEFAULT_COLOR,
      actionMenuId: '',
      removingTag: null,
      error: '',
      createError: ''
    };
  }

  componentDidMount() {
    this.loadTags();
    document.addEventListener('mousedown', this.onDocumentMouseDown);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.onDocumentMouseDown);
  }

  actionMenuRef = React.createRef<HTMLDivElement>();

  onDocumentMouseDown = (event: MouseEvent): void => {
    if (
      this.state.actionMenuId &&
      this.actionMenuRef.current &&
      !this.actionMenuRef.current.contains(event.target as Node)
    ) {
      this.setState({ actionMenuId: '' });
    }
  };

  loadTags = () => {
    const { user } = this.props;
    this.setState({ isLoading: true, error: '' });

    getAllUserTags(user)
      .then(tags => {
        return Promise.all(
          tags.map(tag =>
            getSchoolUsersCount(user, { 'permissions.tagIds': tag.id })
              .then(countObj => ({ ...tag, count: countObj.count }))
              .catch(() => ({ ...tag, count: undefined }))
          )
        );
      })
      .then(items => {
        this.setState({ items, isLoading: false });
      })
      .catch(error => {
        console.error(error);
        this.setState({ isLoading: false, error: 'Unable to load user tags.' });
      });
  };

  normalizeName = (name: string): string =>
    String(name || '')
      .trim()
      .replace(/\s+/g, ' ')
      .slice(0, 40);

  isNameTaken = (name: string, exceptId = ''): boolean => {
    const normalized = this.normalizeName(name).toLowerCase();
    return this.state.items.some(tag => tag.id !== exceptId && tag.tagName.toLowerCase() === normalized);
  };

  createTag = () => {
    const { newName, newColor } = this.state;
    const tagName = this.normalizeName(newName);

    if (!tagName || this.isNameTaken(tagName)) return;

    this.setState({ isLoading: true });
    createUserTag(this.props.user, { tagName, color: newColor })
      .then(() => {
        this.setState(
          {
            isCreateModalOpen: false,
            newName: '',
            newColor: USER_TAG_DEFAULT_COLOR,
            createError: ''
          },
          this.loadTags
        );
      })
      .catch(error => {
        console.error(error);
        this.setState({ isLoading: false, createError: 'Unable to create tag. Please try again.' });
      });
  };

  openCreateModal = (): void => {
    this.setState({
      isCreateModalOpen: true,
      newName: '',
      newColor: USER_TAG_DEFAULT_COLOR,
      createError: ''
    });
  };

  closeCreateModal = (): void => {
    this.setState({
      isCreateModalOpen: false,
      newName: '',
      newColor: USER_TAG_DEFAULT_COLOR,
      createError: ''
    });
  };

  startEdit = (tag: TagWithCount) => {
    this.setState({
      editingId: tag.id,
      editName: tag.tagName,
      editColor: getUserTagColor(tag),
      actionMenuId: ''
    });
  };

  cancelEdit = () => {
    this.setState({ editingId: '', editName: '', editColor: USER_TAG_DEFAULT_COLOR });
  };

  saveEdit = () => {
    const { editingId, editName, editColor } = this.state;
    const tagName = this.normalizeName(editName);

    if (!editingId || !tagName || this.isNameTaken(tagName, editingId)) return;

    this.setState({ isLoading: true });
    updateUserTag(this.props.user, editingId, { tagName, color: editColor })
      .then(() => {
        this.cancelEdit();
        this.loadTags();
      })
      .catch(error => {
        console.error(error);
        this.setState({ isLoading: false, error: 'Unable to update tag.' });
      });
  };

  confirmDelete = () => {
    const { removingTag } = this.state;
    if (!removingTag) return;

    this.setState({ isLoading: true });
    deleteUserTag(this.props.user, removingTag.id)
      .then(() => {
        this.setState({ removingTag: null, editingId: '' });
        this.loadTags();
      })
      .catch(error => {
        console.error(error);
        this.setState({ isLoading: false, error: 'Unable to delete tag.' });
      });
  };

  renderSwatches(value: string, onChange: (color: string) => void): React.ReactNode {
    return (
      <div className="userTags__swatches">
        {USER_TAG_PALETTE.map(color => (
          <button
            key={color}
            type="button"
            className={`userTags__swatch ${value === color ? 'userTags__swatch--on' : ''}`}
            style={{ backgroundColor: color }}
            aria-label={`Choose ${color}`}
            onClick={() => onChange(color)}
          />
        ))}
      </div>
    );
  }

  renderBadge(tag: Partial<Tag>, label?: string): React.ReactNode {
    const color = getUserTagColor(tag);
    return (
      <span className="userTags__badge" style={{ color, backgroundColor: `${color}1A` }}>
        <i style={{ backgroundColor: color }} />
        {label || tag.tagName || 'Tag name'}
      </span>
    );
  }

  renderCreateModal(): React.ReactNode {
    const { isCreateModalOpen, newName, newColor, createError } = this.state;
    const normalizedName = this.normalizeName(newName);
    const duplicate = Boolean(normalizedName && this.isNameTaken(normalizedName));

    return (
      <SimpleModal
        isOpen={isCreateModalOpen}
        title="Create user tag"
        onCloseClick={this.closeCreateModal}
        onBackdropClick={this.closeCreateModal}
        showFooter={false}
        isBodyBold={false}
        customClass="userTagEditorModal"
        dialogStyle={{ maxWidth: 620 }}
      >
        <div className="userTagEditor">
          <p className="userTagEditor__intro">Create a reusable tag for grouping students, staff and contacts.</p>

          <label className="userTagEditor__field">
            <span>Tag name</span>
            <input
              autoFocus
              value={newName}
              maxLength={40}
              placeholder="e.g. Bus route A"
              onChange={event => this.setState({ newName: event.target.value, createError: '' })}
              onKeyDown={event => event.key === 'Enter' && this.createTag()}
            />
            {duplicate ? <em>That tag already exists.</em> : null}
          </label>

          <div className="userTagEditor__field">
            <span>Colour</span>
            {this.renderSwatches(newColor, color => this.setState({ newColor: color }))}
          </div>

          <div className="userTagEditor__preview">
            <span className="userTagEditor__label">Preview</span>
            {this.renderBadge({ color: newColor, tagName: normalizedName || 'Tag name' })}
          </div>

          {createError ? <div className="userTagEditor__error">{createError}</div> : null}

          <div className="userTagEditor__actions">
            <button type="button" className="sit-btn sit-btn--ghost" onClick={this.closeCreateModal}>
              Cancel
            </button>
            <button
              type="button"
              className="sit-btn sit-btn--primary"
              disabled={!normalizedName || duplicate}
              onClick={this.createTag}
            >
              Create tag
            </button>
          </div>
        </div>
      </SimpleModal>
    );
  }

  renderDeleteModal(): React.ReactNode {
    const { removingTag } = this.state;
    if (!removingTag) return null;

    return (
      <div className="modal fade show mDisplayBlock userTagsDeleteModal">
        <div className="modal-dialog modal-md">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Delete tag?</h5>
              <button type="button" className="close" onClick={() => this.setState({ removingTag: null })}>
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <p className="userTags__deleteText">
                {this.renderBadge(removingTag)} is used by <strong>{removingTag.count || 0}</strong>{' '}
                {(removingTag.count || 0) === 1 ? 'person' : 'people'}. Deleting it removes the tag from everyone.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="sit-btn sit-btn--ghost"
                onClick={() => this.setState({ removingTag: null })}
              >
                Cancel
              </button>
              <button type="button" className="sit-btn sit-btn--danger" onClick={this.confirmDelete}>
                Delete tag
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { items, isLoading, query, appliedQuery, editingId, editName, editColor, error } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const normalizedQuery = appliedQuery.trim().toLowerCase();
    const shown = normalizedQuery ? items.filter(tag => tag.tagName.toLowerCase().includes(normalizedQuery)) : items;
    const editNameNormalized = this.normalizeName(editName);
    const editNameTaken = Boolean(editNameNormalized && this.isNameTaken(editNameNormalized, editingId));

    return (
      <div className="userTagsPage schoolPeoplePage">
        {this.renderDeleteModal()}
        {this.renderCreateModal()}
        <PeopleListPageHeader title="User tags" total={items.length}>
          <div className="userTags__headerActions">
            <button type="button" className="sit-btn sit-btn--primary" onClick={this.openCreateModal}>
              <FontAwesomeIcon icon={faPlus} /> Create tag
            </button>
          </div>
        </PeopleListPageHeader>

        <div className="userTags__content">
          {error ? <div className="userTags__error">{error}</div> : null}

          <section className="userTags__card">
            <div className="userTags__listToolbar">
              <div className="userTags__searchWrap">
                <input
                  className="userTags__search"
                  value={query}
                  placeholder="Search tags..."
                  aria-label="Search user tags"
                  onChange={event => this.setState({ query: event.target.value })}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      this.setState({ appliedQuery: query });
                    }
                  }}
                />
                {query ? (
                  <button
                    type="button"
                    aria-label="Clear tag search"
                    onClick={() => this.setState({ query: '', appliedQuery: '' })}
                  >×</button>
                ) : null}
              </div>
            </div>
            <table className="userTags__table">
              <thead>
                <tr>
                  <th>Tag</th>
                  <th>Used by</th>
                  <th className="userTags__actionsHead">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.length ? (
                  shown.map(tag => {
                    const isEditing = editingId === tag.id;
                    return (
                      <tr key={tag.id}>
                        <td>
                          {isEditing ? (
                            <div className="userTags__editRow">
                              <input
                                value={editName}
                                maxLength={40}
                                onChange={event => this.setState({ editName: event.target.value })}
                              />
                              {this.renderSwatches(editColor, color => this.setState({ editColor: color }))}
                              {editNameTaken ? <em>Already exists.</em> : null}
                            </div>
                          ) : (
                            this.renderBadge(tag)
                          )}
                        </td>
                        <td className="userTags__used">
                          {typeof tag.count === 'number' ? tag.count.toLocaleString() : '-'}
                        </td>
                        <td>
                          <div className="userTags__actions">
                            {isEditing ? (
                              <>
                                <button
                                  type="button"
                                  className="sit-btn sit-btn--primary sit-btn--sm"
                                  disabled={!editNameNormalized || editNameTaken}
                                  onClick={this.saveEdit}
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  className="sit-btn sit-btn--ghost sit-btn--sm"
                                  onClick={this.cancelEdit}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <div
                                className="userTags__rowActions"
                                ref={this.state.actionMenuId === tag.id ? this.actionMenuRef : undefined}
                              >
                                <button
                                  type="button"
                                  className="userTags__rowEditButton"
                                  onClick={() => this.startEdit(tag)}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="userTags__rowMenuButton"
                                  aria-label={`Actions for ${tag.tagName}`}
                                  aria-haspopup="menu"
                                  aria-expanded={this.state.actionMenuId === tag.id}
                                  onClick={() =>
                                    this.setState(state => ({
                                      actionMenuId: state.actionMenuId === tag.id ? '' : tag.id
                                    }))
                                  }
                                >
                                  <span aria-hidden="true">•••</span>
                                </button>
                                {this.state.actionMenuId === tag.id ? (
                                  <div className="userTags__rowMenu" role="menu">
                                    <button
                                      type="button"
                                      role="menuitem"
                                      className="isDanger"
                                      onClick={() => this.setState({ actionMenuId: '', removingTag: tag })}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                ) : null}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={3} className="userTags__empty">
                      <ListEmptyState
                        icon={faTags}
                        title={normalizedQuery ? 'No tags match your search' : 'No user tags yet'}
                        description={
                          normalizedQuery
                            ? 'Try another search term or clear the search to see all tags.'
                            : 'Create tags to organise people and build reusable recipient groups.'
                        }
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>
        </div>
      </div>
    );
  }
}
